import { __ } from '@/js/stores/expressions';
import { http } from '../../http';
import {
    RequestPostCoachingForm,
    RequestDeleteCoachingForm,
    RequestGetCoachingForm,
    RequestUpdateCoachingFormInactiveState,
    ResponseGetCoachingForm,
    ResponseGetCoachingFormTable,
    RequestPostCoachingFormRevision,
    ICoachingFormProtocolsUsage,
} from './types';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';

export const COACHING_FORM_VALIDATION_SCOPE = 'coaching-form';

export const getCoachingForm = async (request: RequestGetCoachingForm) => {
    return await http
        .get<ResponseGetCoachingForm>(`/coaching-forms/${request.id}`)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching coaching form:', error);
            throw error;
        });
};

export const createCoachingForm = async (request: RequestPostCoachingForm) => {
    return await http
        .post(
            'coaching-forms',
            request,
            useCustomConfig({
                validationScope: COACHING_FORM_VALIDATION_SCOPE,
                errorMessage: {
                    400: __('error-invalid-reason-codes'),
                    500: __('error-create-coaching-form'),
                    502: __('error-create-coaching-form'),
                },
            })
        )
        .then((response) => response.data);
};

export const getCoachingFormProtocolsUsage = async (request: { id: string }) => {
    return await http
        .get<ICoachingFormProtocolsUsage>(`/coaching-forms/${request.id}/protocols/usage`)
        .then((response) => response.data)
        .catch((error) => {
            console.error('Error fetching coaching form protocols usage:', error);
            throw error;
        });
};

export const updateCoachingForm = async (request: RequestPostCoachingFormRevision) => {
    const { formId, ...body } = request;

    return await http
        .post(
            `coaching-forms/${formId}/revisions`,
            body,
            useCustomConfig({
                validationScope: COACHING_FORM_VALIDATION_SCOPE,
                errorMessage: {
                    400: __('error-invalid-reason-codes'),
                    500: __('error-update-coaching-form'),
                    502: __('error-update-coaching-form'),
                },
            })
        )
        .then((response) => response.data);
};

export const getCoachingFormTable = async (params?: { includeInactive?: boolean }) => {
    return http
        .get<ResponseGetCoachingFormTable>(
            '/coaching-forms',
            useCustomConfig({
                params: {
                    includeInactive: params?.includeInactive ?? false,
                },
            })
        )
        .then((response) => ({
            ...response.data,
            forms: response.data.forms.map((form) => ({
                ...form,
                title: form.title?.trim() ? form.title : __('coaching-form-untitled'),
            })),
        }))
        .catch((error) => {
            console.error('Error fetching coaching form table', error);
            throw error;
        });
};

export const deleteCoachingForm = async (
    req: RequestDeleteCoachingForm,
    { force = false }: { force?: boolean } = {}
) => {
    return http
        .delete<void>(
            `/coaching-forms/${req.id}`,
            useCustomConfig({
                params: { force },
            })
        )
        .catch((error) => {
            console.error('Error deleting coaching form', error);
            throw error;
        });
};

export const updateCoachingFormInactiveState = async (
    req: RequestUpdateCoachingFormInactiveState
) => {
    return http
        .patch<void>(
            `/coaching-forms/${req.id}/inactive-state`,
            { isInactive: req.isInactive },
            useCustomConfig({
                errorMessage: {
                    500: __('error-update-coaching-form'),
                    502: __('error-update-coaching-form'),
                },
            })
        )
        .then((response) => response.data);
};
